import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import bedrockService from '../services/bedrockService';
import mcpService from '../services/mcpService';
import { awsConfig } from '../aws-config';
import './ChatAssistant.css';

const ChatAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  const connectToGateway = async () => {
    try {
      const gatewayUrl = awsConfig.Agent.gatewayUrl;
      if (!gatewayUrl || gatewayUrl.includes('INJECT_')) {
        throw new Error('Agent gateway URL not configured');
      }

      await mcpService.connect(gatewayUrl);
      const toolSpecs = mcpService.getBedrockToolSpecs();
      bedrockService.setToolSpecs(toolSpecs);
      setIsConnected(true);
      setError(null);

      setMessages([{
        role: 'assistant',
        content: 'Hello! I\'m your Chulas Assistant. I can help you with questions about the admin portal, data management, and general assistance. How can I help you today?',
        timestamp: new Date()
      }]);
    } catch (err) {
      console.error('Failed to connect to gateway:', err);
      setError('Failed to connect to assistant service. Please try again later.');
      setIsConnected(false);
    }
  };

  const handleOpen = async () => {
    setIsOpen(true);
    if (!isConnected && messages.length === 0) {
      await connectToGateway();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const executeTool = async (toolUse) => {
    try {
      const result = await mcpService.callTool(
        awsConfig.Agent.gatewayUrl,
        toolUse.name,
        toolUse.input
      );

      return {
        toolUseId: toolUse.toolUseId,
        content: result.content || []
      };
    } catch (error) {
      console.error('Tool execution failed:', error);
      return {
        toolUseId: toolUse.toolUseId,
        content: [{
          type: 'text',
          text: `Error executing tool: ${error.message}`
        }],
        status: 'error'
      };
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);

    try {
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: [{ text: msg.content }]
      }));

      conversationHistory.push({
        role: 'user',
        content: [{ text: inputMessage }]
      });

      let response = await bedrockService.sendMessageStream(
        conversationHistory,
        'global.anthropic.claude-haiku-4-5-20251001-v1:0',
        'You are the Chulas Assistant, a helpful AI assistant for the Chulas admin portal. You help administrators manage users, events, specials, menu items, announcements, and valet services. Be concise and helpful.',
        null
      );

      while (response.stopReason === 'tool_use') {
        const toolUses = response.content.filter(c => c.toolUse);
        const toolResults = [];

        for (const contentBlock of toolUses) {
          const result = await executeTool(contentBlock.toolUse);
          toolResults.push({
            toolResult: result
          });
        }

        conversationHistory.push({
          role: 'assistant',
          content: response.content
        });

        conversationHistory.push({
          role: 'user',
          content: toolResults
        });

        response = await bedrockService.sendMessage(
          conversationHistory,
          'global.anthropic.claude-haiku-4-5-20251001-v1:0'
        );
      }

      const assistantContent = response.content
        .filter(c => c.text)
        .map(c => c.text)
        .join('');

      const assistantMessage = {
        role: 'assistant',
        content: assistantContent,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Failed to send message:', err);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) {
    return (
      <div className="chat-assistant-button" onClick={handleOpen}>
        <span className="chat-icon">💬</span>
        <span className="chat-label">Assistant</span>
      </div>
    );
  }

  return (
    <div className={`chat-assistant-container ${isMinimized ? 'minimized' : ''}`}>
      <div className="chat-assistant-header">
        <div className="chat-header-title">
          <span className="chat-icon">💬</span>
          <span>Chulas Assistant</span>
        </div>
        <div className="chat-header-actions">
          <button onClick={handleMinimize} className="chat-header-button">
            {isMinimized ? '□' : '_'}
          </button>
          <button onClick={handleClose} className="chat-header-button">
            ✕
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          <div className="chat-assistant-messages">
            {error && (
              <div className="chat-error-banner">
                {error}
                <button onClick={connectToGateway} className="retry-button">
                  Retry
                </button>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div key={idx} className={`chat-message ${msg.role} ${msg.isError ? 'error' : ''}`}>
                <div className="message-role">
                  {msg.role === 'user' ? 'You' : 'Assistant'}
                </div>
                <div className="message-content">
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {msg.content}
                    </ReactMarkdown>
                  ) : (
                    <p>{msg.content}</p>
                  )}
                </div>
                <div className="message-timestamp">
                  {msg.timestamp.toLocaleTimeString()}
                </div>
              </div>
            ))}

            {loading && (
              <div className="chat-message assistant loading">
                <div className="message-role">Assistant</div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="chat-assistant-input">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything..."
              disabled={loading || !isConnected}
              rows={1}
            />
            <button
              onClick={sendMessage}
              disabled={loading || !inputMessage.trim() || !isConnected}
              className="send-button"
            >
              ➤
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ChatAssistant;
